import { getModel } from '../../model/registry'
import { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { useQuery } from '@tanstack/react-query'

import useApi from '../../hooks/useApi'
import useTranslation from '../../hooks/useTranslation'
import buildQueryString from '../../utils/api/buildQueryString'
import { qk } from '../../utils/queryKeys'
import { getFromLS, saveToLS } from '../../utils/localStore/localStore'
import { SkeletonBase } from '../generic/SkeletonLoader'
import generateSetValueObject from './generateSetValueObject'
import CardOptions, { Row, SIZES } from './CardOptions'

// One localStorage bucket for every persisting picker; `persistKey` is the key
// inside it. Same shape as the other localStore users ('orgSwitcher', 'version').
const LS_ID = 'cardSelect'

const Message = styled.span`
    font-size: var(--font-XS);
    color: ${({ theme }) => theme.font.label};
`

const SkeletonCard = styled(SkeletonBase)`
    flex: 0 0 auto;
    min-width: ${({ $size }) => $size.minWidth};
    height: ${({ $size }) => $size.title === 'var(--font-M)' ? '3.75rem' : '2.75rem'};
    border-radius: 0.5rem;
    margin-bottom: 0;
`

/**
 * CardSelect — pick one record from a short list shown as a row of cards.
 *
 * Where `connect` opens a modal to search a long list, this shows every option
 * at once: a bold identifier (a code) over a smaller name. Use it when the list
 * is small enough to scan — warehouses, sites, price lists — and the choice
 * frames everything else on the page.
 *
 * Which entity it reads, which two fields it shows, and how the choice maps back
 * onto form fields all live in `model/components/cardSelectData.jsx`, named by
 * `configRef`. That is what makes the component generic: this file knows how a
 * card looks and nothing about warehouses.
 *
 * It works in two shapes:
 *   - standalone (a page header): pass `valueId` and `onSelect`, and the caller
 *     receives the whole record.
 *   - inside a cardView: `FormElem` passes `setValue`, and the config's
 *     `valueMap` decides which form fields the selection writes, exactly as a
 *     `connect` field does.
 */
const CardSelect = ({
    configRef,
    config: configProp,
    valueId,
    setValue,
    onSelect,
    size,
    entityParams,
    linkedFields,
    isRed,
    testId,
}) => {
    const { getMe } = useApi()
    const { t } = useTranslation()

    const config = configProp || getModel('cardSelectData')[configRef]

    // What the caller has already been told about. Guards the resolve effect
    // below from re-reporting a selection the caller just made.
    const reported = useRef(null)
    const restored = useRef(false)

    const entity = config?.entity
    const params = entityParams || config?.entityParams
    const queryString = params ? buildQueryString(params, linkedFields) : null

    const getData = async () => {
        const resource = queryString ? `${entity}${queryString}` : `${entity}`
        const response = await getMe(resource)
        return Array.isArray(response?.data) ? response.data : []
    }

    // Same key as Connect, so a card picker and a modal picker on one entity
    // share a single cached list.
    const result = useQuery({
        queryKey: qk.connect(entity, queryString),
        queryFn: getData,
        keepPreviousData: true,
        enabled: !!entity,
    })

    const titleField = config?.titleField || 'code'
    const subField = config?.subField
    const persistKey = config?.persistKey
    const scale = SIZES[size || config?.size] || SIZES.medium

    const list = result.data || []
    const orderBy = config?.orderBy || titleField
    const options = [...list].sort((a, b) =>
        String(a?.[orderBy] ?? '').localeCompare(String(b?.[orderBy] ?? '')))

    const select = (record) => {
        reported.current = record.id
        if (persistKey) saveToLS(LS_ID, persistKey, record.id)
        if (setValue && config?.valueMap) setValue(generateSetValueObject(record, config.valueMap))
        if (onSelect) onSelect(record)
    }

    // Resolve the current selection to a record once the list arrives. Covers
    // both an id the caller already holds (a URL param, a saved form field) and
    // the one restored from the last visit — the caller needs the record either
    // way, and only this component has the list to look it up in.
    useEffect(() => {
        if (!options.length) return

        if (valueId) {
            if (reported.current === valueId) return
            const match = options.find(r => r.id === valueId)
            reported.current = valueId
            // A stored id that no longer resolves is dropped rather than
            // handed on, so a deleted record cannot come back next visit.
            if (match) select(match)
            else if (persistKey) saveToLS(LS_ID, persistKey, '')
            return
        }

        if (!persistKey || restored.current) return
        restored.current = true
        const stored = getFromLS(LS_ID, persistKey)
        const match = stored && options.find(r => r.id === stored)
        if (match) select(match)
        // Resolution is by id, so the list only matters when it arrives or
        // changes length — its contents changing cannot change the answer.
    }, [options.length, valueId, persistKey])

    if (!config) return null

    if (result.isLoading) {
        return (
            <Row $size={scale}>
                <SkeletonCard $size={scale} />
                <SkeletonCard $size={scale} />
                <SkeletonCard $size={scale} />
            </Row>
        )
    }

    if (result.isError || !options.length) {
        return (
            <Row $size={scale}>
                <Message>{t(result.isError ? 'error.network' : (config.emptyMessage || 'no.results.found'))}</Message>
            </Row>
        )
    }

    // The cards themselves are CardOptions — one look for every card picker.
    return (
        <CardOptions
            options={options.map(record => ({ id: record.id, title: record[titleField], subtitle: subField ? record[subField] : null }))}
            value={valueId}
            onChange={(id) => { const record = options.find(r => r.id === id); if (record) select(record) }}
            size={size || config?.size}
            isRed={isRed}
            label={config.label}
            testId={testId}
        />
    )
}

export default CardSelect
